import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { isWithinInterval } from "date-fns";
import { HttpClient } from "../../api/httpClient";
import { getDateRange } from "../../utils/more/date_functions";
import normalize from "../../utils/normalize";

const baseName = "travels";

const adapters = {
  personal: createEntityAdapter({
    selectId: (entity) => entity._id,
    sortComparer: (a, b) => b?.createdAt?.localeCompare(a?.createdAt),
  }),
  approver: createEntityAdapter({
    selectId: (entity) => entity._id,
    sortComparer: (a, b) => b?.sentAt?.localeCompare(a?.sentAt),
  }),
  financial: createEntityAdapter({
    selectId: (entity) => entity._id,
    sortComparer: (a, b) => b?.createdAt?.localeCompare(a?.createdAt),
  }),
  director: createEntityAdapter({
    selectId: (entity) => entity._id,
    sortComparer: (a, b) => b?.createdAt?.localeCompare(a?.createdAt),
  }),
};

const DEFAULT_ADAPTER_INITIAL_STATE = {
  status: "idle",
  error: null,
};

const initialState = {
  personal: adapters.personal.getInitialState(DEFAULT_ADAPTER_INITIAL_STATE),
  approver: adapters.approver.getInitialState(DEFAULT_ADAPTER_INITIAL_STATE),
  financial: adapters.financial.getInitialState(DEFAULT_ADAPTER_INITIAL_STATE),
  director: adapters.financial.getInitialState(DEFAULT_ADAPTER_INITIAL_STATE),
};

export const getTravels = createAsyncThunk(
  `${baseName}/getTravels`,
  async ({ role, clearCache, signal, filters = {}, status }) => {
    const { date } = filters;
    const { type = "this-month", meta = {} } = date || {};
    const [fromDate, toDate] = getDateRange(type, meta);
    let params = { role, status, fromDate, toDate };
    if (role === "personal" || role === "approver") {
      delete params.status;
      delete params.fromDate;
      delete params.toDate;
    }
    if (status === "approved" && role === "financial") {
      delete params.fromDate;
      delete params.toDate;
    }
    const res = await HttpClient.get({
      url: "/travels",
      params: {
        ...params,
        status: status === "all" ? undefined : status,
      },
      shouldCache: !clearCache,
      signal,
    });
    return res?.data;
  }
);

export const travelsSlice = createSlice({
  name: baseName,
  initialState,
  reducers: {
    addTravel(state, { payload }) {
      const { role, data } = payload;
      if (adapters[role] && state[role]) {
        adapters[role].addOne(state[role], {
          ...data,
          id: data?._id || data?.id,
        });
      }
    },
    removeManyTravels(state, { payload }) {
      const { role, data } = payload;
      if (adapters[role] && state[role]) {
        adapters[role].removeMany(state[role], data);
      }
    },
    updateManyTravels(state, { payload }) {
      const { role, ids, changes } = payload;
      if (adapters[role] && state[role]) {
        adapters[role].updateMany(
          state[role],
          ids?.map((expenseId) => ({ id: expenseId, changes }))
        );
      }
    },
    updateTravel(state, { payload }) {
      const { role, id, changes } = payload;
      if (adapters[role] && state[role]) {
        adapters[role].updateOne(state[role], {
          id,
          changes,
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTravels.pending, (state, action) => {
        const { role } = action.meta.arg;
        state[role].status = "loading";
      })
      .addCase(getTravels.rejected, (state, action) => {
        const { role } = action.meta.arg;
        if (!state[role]) return;
        state[role].status = "failed";
        state[role].error =
          action.error.message || "Não foi possível buscar as viagens";
      })
      .addCase(getTravels.fulfilled, (state, action) => {
        const { role } = action.meta.arg;
        const advances = action.payload || [];
        if (!state[role]) return;
        state[role].status = "succeeded";
        const { entities } = normalize(advances);
        adapters[role].setAll(state[role], entities);
      });
  },
});

export const { addTravel, removeManyTravels, updateManyTravels, updateTravel } =
  travelsSlice.actions;

export const travelsSelectors = {
  personal: adapters.personal.getSelectors(
    (state) => state[baseName]?.personal
  ),
  approver: adapters.approver.getSelectors(
    (state) => state[baseName]?.approver
  ),
  financial: adapters.financial.getSelectors(
    (state) => state[baseName]?.financial
  ),
  director: adapters.director.getSelectors(
    (state) => state[baseName]?.director
  ),
};

export const selectTravelsByFilter = createSelector(
  [
    (state, role) => travelsSelectors[role].selectAll(state),
    (state, role, filter, date) => ({
      role,
      filter,
      date,
    }),
  ],
  (travels = [], payload = {}) => {
    const { role, filter, date } = payload;

    if (role === "director") return [...travels];
    if (role === "financial") return [...travels];

    let filtered = [...travels];
    if (role === "personal") {
      switch (filter) {
        case "all":
          filtered = [...travels];
          break;
        // case "for_me":
        //   filtered = travels?.filter((travel) => travel?.type === "regular");
        //   break;
        // case "refund":
        //   filtered = travels?.filter((travel) => travel?.type === "refund");
        //   break;
        default:
          filtered = [...travels];
          break;
      }
    }
    if (date) {
      const [fromDate, toDate] = getDateRange(date?.type, date?.meta);
      filtered = filtered?.filter((travel) =>
        isWithinInterval(new Date(travel?.createdAt), {
          start: new Date(fromDate),
          end: new Date(toDate),
        })
      );
    }

    return filtered;
  }
);

export default travelsSlice.reducer;
